import { z } from 'zod'

export type SectionKey = string

export type SectionRegistryEntry<Schema extends z.ZodTypeAny> = {
	readonly key: SectionKey
	readonly version: number
	readonly schema: Schema
	readonly createDefault: () => z.infer<Schema>
	readonly migrate?: (
		draft: unknown,
		context: { readonly fromVersion: number; readonly toVersion: number },
	) => z.infer<Schema>
}

const sectionRegistry = new Map<
	SectionKey,
	SectionRegistryEntry<z.ZodTypeAny>
>()

export const CURRENT_INTAKE_VERSION = 1

export const IntakeDraftEnvelopeSchema = z
	.object({
		version: z.number().int().min(0).default(CURRENT_INTAKE_VERSION),
		sections: z.record(z.string(), z.unknown()).default({}),
	})
	.default({})

export type IntakeDraftEnvelope = z.infer<typeof IntakeDraftEnvelopeSchema>

export type IntakeDraft = IntakeDraftEnvelope['sections']

export function registerSection<Schema extends z.ZodTypeAny>(
	entry: SectionRegistryEntry<Schema>,
) {
	if (sectionRegistry.has(entry.key)) {
		throw new Error(
			`Section "${entry.key}" is already registered in the intake registry.`,
		)
	}
	sectionRegistry.set(entry.key, entry)
}

export function getSectionRegistry() {
	return sectionRegistry
}

export function getRegisteredSectionKeys(): SectionKey[] {
	return [...sectionRegistry.keys()]
}

export function createSectionDefaults(): Record<SectionKey, unknown> {
	const defaults: Record<SectionKey, unknown> = {}
	for (const [key, entry] of sectionRegistry.entries()) {
		try {
			defaults[key] = entry.createDefault()
		} catch (error) {
			console.warn(
				`Failed to create default draft for intake section "${key}".`,
				error,
			)
		}
	}
	return defaults
}

export function migrateSectionDraft(
	key: SectionKey,
	raw: unknown,
	storedVersion: number,
	targetVersion: number,
): unknown {
	const entry = sectionRegistry.get(key)
	if (!entry) {
		return raw ?? null
	}
	const baseVersion = Math.min(storedVersion, targetVersion, entry.version)
	if (
		typeof entry.migrate === 'function' &&
		storedVersion < entry.version &&
		entry.version <= targetVersion
	) {
		try {
			return entry.migrate(raw, {
				fromVersion: baseVersion,
				toVersion: targetVersion,
			})
		} catch (error) {
			console.warn(
				`MoCRA intake section migration failed for "${key}". Falling back to defaults.`,
				error,
			)
			return entry.createDefault()
		}
	}
	try {
		return entry.schema.parse(raw)
	} catch {
		return entry.createDefault()
	}
}

export function mergeWithSectionDefaults(
	sections: Record<SectionKey, unknown>,
	storedVersion: number,
): Record<SectionKey, unknown> {
	const defaults = createSectionDefaults()
	const merged: Record<SectionKey, unknown> = { ...defaults }
	for (const [key, value] of Object.entries(sections)) {
		merged[key] = migrateSectionDraft(
			key,
			value,
			storedVersion,
			CURRENT_INTAKE_VERSION,
		)
	}
	return merged
}
